import { DateTimeRangePicker } from '@mui/x-date-pickers-pro/DateTimeRangePicker';
import { DateTime } from 'luxon';
import { memo, ReactElement, useCallback, useMemo } from 'react';
import { BaseFilterProps } from './common';
import { DateFilterDTO, FilterDateRangeOptionDTO } from '../../../api/generated';
import { skipUndefined } from '../../../utils';
import { MultiInputDateTimeRangeField } from '@mui/x-date-pickers-pro';

export type DateRangeFilterProps = BaseFilterProps<DateFilterDTO, FilterDateRangeOptionDTO>;

export const DateRangeFilter = memo(function ({
  value,
  onValueChange,
  onCommit,
  filterOptions,
}: DateRangeFilterProps): ReactElement {
  const isDisabled = useMemo(() => !filterOptions, [filterOptions]);
  const startDate = useMemo<DateTime | null>(() => skipUndefined(value?.range?.gte, DateTime.fromISO) || null, [value]);
  const endDate = useMemo<DateTime | null>(() => skipUndefined(value?.range?.lt, DateTime.fromISO) || null, [value]);
  const minDate = useMemo<DateTime | undefined>(
    () => skipUndefined(filterOptions?.min, DateTime.fromISO),
    [filterOptions]
  );
  const maxDate = useMemo<DateTime | undefined>(
    () => skipUndefined(filterOptions?.max, DateTime.fromISO),
    [filterOptions]
  );

  const handleDateChange = useCallback(
    function ([startDate, endDate]: [DateTime | null, DateTime | null]) {
      if (!!startDate || !!endDate) {
        onValueChange({
          range: {
            gte: startDate?.toISO({ includeOffset: true }) || undefined,
            lt: endDate?.toISO({ includeOffset: true }) || undefined,
          },
        });
      } else {
        onValueChange(undefined);
      }
    },
    [onValueChange]
  );

  return (
    <DateTimeRangePicker
      views={['day', 'hours', 'minutes']}
      minDate={minDate}
      maxDate={maxDate}
      disableFuture
      ampm={false}
      displayWeekNumber
      sx={{
        '& .MuiFormControl-root': {
          margin: '0',
        },
        width: '90%',
      }}
      slots={{ field: MultiInputDateTimeRangeField }}
      slotProps={{
        field: {
          direction: 'column',
          spacing: 0.5,
          dateSeparator: '',
          sx: {
            width: '100%',
          },
        } as any, // cast to any. I am sure this is correct, but types are wrong in the library
        textField: {
          size: 'small',
          fullWidth: true,
        },
      }}
      onChange={handleDateChange}
      onClose={onCommit}
      value={[startDate, endDate]}
      disabled={isDisabled}
    />
  );
});
